import React, { ReactElement, useCallback, useMemo, useState } from "react";

import styled, { createGlobalStyle } from "styled-components";

import { Button } from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { CounterButton } from "../../components/CounterButton";
import { Dialog } from "../../components/Dialog";
import { IconButton } from "../../components/IconButton";
import {
  AddCrossTinyIcon,
  CloseSmallIcon,
  CrownBlankIcon,
  CrownSelectedIcon,
  PeopleIcon,
  SortGeneralIcon,
} from "../../components/Icons";
import { Label } from "../../components/Label";
import { List, ListItem } from "../../components/List";
import { Menu, MenuHeader } from "../../components/Menu";
import { Select } from "../../components/Select";
import { SelectTrigger } from "../../components/SelectTrigger";
import { Textarea } from "../../components/Textarea";
import { Tooltip } from "../../components/Tooltip";
import { Body2, Caption1 } from "../../components/Typography";
import { AVATAR_COLORS, RoundAvatar } from "../shared";

/* ------------------------------------------------------------------ */
/* People menu (copy of the project header People popover)             */
/* ------------------------------------------------------------------ */

export interface BoardMember {
  id: number;
  name: string;
  initials: string;
  color: string;
  isClient?: boolean;
}

/* 12 people (9 members + 3 clients) so the 330px list visibly scrolls.
   Jasna (id 1) starts as the board manager. */
export const BOARD_MEMBERS: BoardMember[] = [
  { id: 1, name: "Jasna Simic", initials: "JS", color: AVATAR_COLORS.purple },
  { id: 2, name: "Marko Antic", initials: "MA", color: AVATAR_COLORS.teal },
  { id: 3, name: "Dario Kovac", initials: "DK", color: AVATAR_COLORS.orange },
  { id: 4, name: "Ivana Lukic", initials: "IL", color: AVATAR_COLORS.green },
  { id: 5, name: "Petar Novak", initials: "PN", color: AVATAR_COLORS.blue },
  { id: 6, name: "Sara Milic", initials: "SM", color: AVATAR_COLORS.yellow },
  { id: 7, name: "Luka Peric", initials: "LP", color: AVATAR_COLORS.purple },
  { id: 8, name: "Mia Radic", initials: "MR", color: AVATAR_COLORS.teal },
  { id: 9, name: "Nikola Vukic", initials: "NV", color: AVATAR_COLORS.orange },
  {
    id: 10,
    name: "Ana Klein",
    initials: "AK",
    color: AVATAR_COLORS.green,
    isClient: true,
  },
  {
    id: 11,
    name: "Tom Becker",
    initials: "TB",
    color: AVATAR_COLORS.blue,
    isClient: true,
  },
  {
    id: 12,
    name: "Eva Fischer",
    initials: "EF",
    color: AVATAR_COLORS.yellow,
    isClient: true,
  },
];

/* Mirrors ProjectPeopleWrapper + view.project_top_bar.less:
   Menu 260px, "People" MenuHeader with an (inert) "+", 330px scroll area,
   Members/Clients Caption1 section labels, rows with hover-only options. */
export const StyledPeopleMenu = styled.div`
  .people-scroll {
    max-height: 330px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 8px;
  }

  .people-section-label {
    display: block;
    margin: 8px 0;
    padding: 0 16px;
  }

  /* .project-members li overrides: radius, 8px h-padding, tw-mx-2 */
  .member-item {
    justify-content: space-between;
    margin: 4px 8px;
    padding: 0 8px;
    border-radius: 4px;
    cursor: auto;
  }

  /* options hidden until row hover (.project-member-options pattern) */
  .member-options {
    display: none;
    align-items: center;
  }

  .member-item:hover .member-options {
    display: flex;
  }

  .member-side {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  /* UserLink: avatar + name, 8px gap, truncated (2px v-padding) */
  .member-user {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
    overflow: hidden;

    .member-name {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  /* gold ring on the manager's avatar (.project-leader-avatar) */
  .manager-avatar {
    outline: 2px #ffcc6b solid;
    outline-offset: -2px;
  }
`;

export interface PeopleMenuSectionProps {
  label: string;
  people: BoardMember[];
  managerId: number;
  onMakeManager: (person: BoardMember) => void;
  onRemove: (person: BoardMember) => void;
}

/* Rows sorted by name. Manager: gold crown + ring, no hover options (the
   role must be handed over before removal). Crown option only on non-client
   rows; X on everyone else. */
export const PeopleMenuSection = ({
  label,
  people,
  managerId,
  onMakeManager,
  onRemove,
}: PeopleMenuSectionProps): ReactElement => (
  <>
    <Caption1 weight="bold" className="people-section-label">
      {label}
    </Caption1>
    <List>
      {[...people]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((person) => {
          const isManager = person.id === managerId;

          return (
            <ListItem key={person.id} className="member-item">
              <span className="member-user">
                <RoundAvatar
                  $bg={person.color}
                  $bordered={false}
                  className={isManager ? "manager-avatar" : undefined}
                >
                  {person.initials}
                </RoundAvatar>
                <Body2 className="member-name">{person.name}</Body2>
              </span>
              <span className="member-side">
                {isManager ? <CrownSelectedIcon fill="#FFCC6B" /> : null}
                <span className="member-options">
                  {!isManager && !person.isClient ? (
                    <Tooltip title="Set as Whiteboard Manager">
                      <IconButton
                        size="small"
                        variant="text gray"
                        onClick={() => onMakeManager(person)}
                      >
                        <CrownBlankIcon />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {!isManager ? (
                    <Tooltip title="Remove from Whiteboard">
                      <IconButton
                        size="small"
                        variant="text gray"
                        onClick={() => onRemove(person)}
                      >
                        <CloseSmallIcon />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </span>
              </span>
            </ListItem>
          );
        })}
    </List>
  </>
);

/* ------------------------------------------------------------------ */
/* Add-members dialog (people "+" → DS Dialog + invite-style picker)   */
/* ------------------------------------------------------------------ */

export interface InviteCandidate {
  id: number;
  name: string;
  initials: string;
  color: string;
}

export interface InviteCompany {
  name: string;
  isOwnerCompany?: boolean;
  people: InviteCandidate[];
}

/* Owner company first, remaining companies sorted by name; people sorted
   by name within each company — same ordering as the real user picker.
   Members of non-owner companies join the board as clients. */
export const INVITE_COMPANIES: InviteCompany[] = [
  {
    name: "ActiveCollab",
    isOwnerCompany: true,
    people: [
      { id: 101, name: "Ana Ilic", initials: "AI", color: AVATAR_COLORS.green },
      {
        id: 102,
        name: "Bojan Peric",
        initials: "BP",
        color: AVATAR_COLORS.blue,
      },
      {
        id: 103,
        name: "Goran Simovic",
        initials: "GS",
        color: AVATAR_COLORS.orange,
      },
      {
        id: 104,
        name: "Vera Antic",
        initials: "VA",
        color: AVATAR_COLORS.purple,
      },
    ],
  },
  {
    name: "Bitlab Studio",
    people: [
      {
        id: 201,
        name: "Elena Weber",
        initials: "EW",
        color: AVATAR_COLORS.teal,
      },
      {
        id: 202,
        name: "Jonas Richter",
        initials: "JR",
        color: AVATAR_COLORS.yellow,
      },
    ],
  },
  {
    name: "Nova Consulting",
    people: [
      {
        id: 301,
        name: "Iris Novak",
        initials: "IN",
        color: AVATAR_COLORS.blue,
      },
      {
        id: 302,
        name: "Karlo Babic",
        initials: "KB",
        color: AVATAR_COLORS.green,
      },
      {
        id: 303,
        name: "Tea Grgic",
        initials: "TG",
        color: AVATAR_COLORS.purple,
      },
    ],
  },
];

/* The Select panel and the Dialog render through portals, so they can only
   be styled via real global classes — wrapper descendant selectors can't
   reach them. Actions gap 12px = the app's "tw-flex tw-gap-3" on
   Dialog.Actions (InviteToProjectsDialog). */
export const AddMembersDialogStyle = createGlobalStyle`
  .wb-user-select {
    width: 480px;
    max-width: calc(100vw - 32px);
  }

  .wb-dialog-actions {
    display: flex;
    gap: 12px;
  }
`;

export const StyledCandidateRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-grow: 1;

  .candidate-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

/* same offset the Select's built-in row checkbox has */
export const StyledRowCheckbox = styled(Checkbox)`
  margin-left: 12px;
`;

export const StyledDialogFields = styled.div`
  display: flex;
  flex-direction: column;

  .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .field + .field {
    margin-top: 16px;
  }

  /* DS fixed widths (SelectTrigger 300px, Textarea 360px) → full width */
  button.wb-user-trigger {
    width: 100%;
  }

  textarea.wb-invite-message {
    width: 100%;
    box-sizing: border-box;
  }
`;

export interface AddMembersDialogProps {
  open: boolean;
  onClose: () => void;
  /** ids of current board members — excluded from the picker */
  existingIds: number[];
  onAdd: (people: BoardMember[]) => void;
}

/* Closes on Esc (DS Dialog default) and Cancel; background click stays
   disabled (enableBackgroundClick not set). Add is disabled until at least
   one user is selected; on Add the selection joins the members list (in the
   real app this also sends the invitation email with the optional message,
   and Add stays disabled while the request is in flight). */
export const AddMembersDialog = ({
  open,
  onClose,
  existingIds,
  onAdd,
}: AddMembersDialogProps): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [message, setMessage] = useState("");

  /* current members drop out of the picker; companies left empty disappear */
  const options = useMemo(
    () =>
      INVITE_COMPANIES.map((company, index) => ({
        id: 1000 + index,
        name: company.name,
        options: company.people
          .filter((person) => !existingIds.includes(person.id))
          .map((person) => ({ ...person })),
      })).filter((company) => company.options.length > 0),
    [existingIds]
  );

  const triggerText =
    selectedIds.length === 0
      ? "Choose users"
      : `${selectedIds.length} ${
          selectedIds.length === 1 ? "user" : "users"
        } selected`;

  const handleAdd = useCallback(() => {
    const added = INVITE_COMPANIES.flatMap((company) =>
      company.people
        .filter((person) => selectedIds.includes(person.id))
        .map((person) => ({
          id: person.id,
          name: person.name,
          initials: person.initials,
          color: person.color,
          isClient: !company.isOwnerCompany,
        }))
    );

    onAdd(added);
    setSelectedIds([]);
    setMessage("");
    onClose();
  }, [onAdd, onClose, selectedIds]);

  return (
    <Dialog open={open} onClose={onClose}>
      <AddMembersDialogStyle />
      <Dialog.Title>Add Users</Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        <StyledDialogFields>
          <div className="field">
            <Label htmlFor="wb-users" required>
              Users
            </Label>
            <Select
              type="multiple"
              options={options}
              selected={selectedIds}
              onChange={(value) =>
                setSelectedIds(Array.isArray(value) ? value : [])
              }
              noResultText="No results"
              selectClassName="wb-user-select"
              target={
                <SelectTrigger
                  id="wb-users"
                  className="wb-user-trigger"
                  typographyProps={{
                    color: selectedIds.length > 0 ? "primary" : "quaternary",
                  }}
                >
                  {triggerText}
                </SelectTrigger>
              }
              endAdornment={
                <Tooltip title="Group by">
                  <IconButton variant="text gray">
                    <SortGeneralIcon />
                  </IconButton>
                </Tooltip>
              }
              renderOption={(option, data) => (
                <>
                  <StyledCandidateRow>
                    <RoundAvatar
                      $bg={String(option.color)}
                      $size={24}
                      $bordered={false}
                    >
                      {option.initials}
                    </RoundAvatar>
                    <Body2 className="candidate-name">{option.name}</Body2>
                  </StyledCandidateRow>
                  <StyledRowCheckbox
                    {...(data as {
                      id?: string;
                      checked?: boolean;
                      onChange?: () => void;
                    })}
                  />
                </>
              )}
            />
          </div>
          <div className="field">
            <Label htmlFor="wb-invite-message">Message to Users</Label>
            <Textarea
              id="wb-invite-message"
              className="wb-invite-message"
              placeholder="Short note to invited users"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
            <Caption1 color="tertiary">
              Let people know what this whiteboard is for and why you&apos;re
              inviting them. If provided, this message is included in the
              invitation email.
            </Caption1>
          </div>
        </StyledDialogFields>
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions className="wb-dialog-actions">
        <Button
          variant="primary"
          disabled={selectedIds.length === 0}
          onClick={handleAdd}
        >
          Add
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

/* People trigger (icon + member count) + the popover + the dialogs.

   Flows mirror the implementation spec. The mock acts as a non-owner
   manager, so a crown click always shows the confirm dialog (owners would
   transfer immediately), and hover options stay visible after a transfer so
   the flows can be replayed. */
export const WhiteboardPeopleMenu = (): ReactElement => {
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const [boardMembers, setBoardMembers] = useState(BOARD_MEMBERS);
  const [managerId, setManagerId] = useState(1);
  const [managerTarget, setManagerTarget] = useState<BoardMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<BoardMember | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleDialogOpen = useCallback(() => {
    setOpen(false);
    setDialogOpen(true);
  }, []);
  const handleDialogClose = useCallback(() => setDialogOpen(false), []);

  const handleAddMembers = useCallback((added: BoardMember[]) => {
    setBoardMembers((current) => [...current, ...added]);
  }, []);

  /* close the menu before opening any dialog (menus-that-open-dialogs rule) */
  const handleMakeManager = useCallback((person: BoardMember) => {
    setOpen(false);
    setManagerTarget(person);
  }, []);

  const handleRemove = useCallback((person: BoardMember) => {
    setOpen(false);
    setRemoveTarget(person);
  }, []);

  const handleManagerConfirm = useCallback(() => {
    if (managerTarget) {
      setManagerId(managerTarget.id);
    }
    setManagerTarget(null);
  }, [managerTarget]);

  const handleRemoveConfirm = useCallback(() => {
    if (removeTarget) {
      setBoardMembers((current) =>
        current.filter((person) => person.id !== removeTarget.id)
      );
    }
    setRemoveTarget(null);
  }, [removeTarget]);

  const members = boardMembers.filter((person) => !person.isClient);
  const clients = boardMembers.filter((person) => person.isClient);

  return (
    <>
      <AddMembersDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        existingIds={boardMembers.map((person) => person.id)}
        onAdd={handleAddMembers}
      />
      <ConfirmDialog
        open={managerTarget !== null}
        dialogTitle="Change Whiteboard Manager"
        dialogContent="By changing the Whiteboard Manager, you'll lose the ability to manage this whiteboard."
        confirmBtnText="Change"
        shouldShowCancelButton
        onConfirm={handleManagerConfirm}
        onCancel={() => setManagerTarget(null)}
      />
      <ConfirmDialog
        open={removeTarget !== null}
        dialogTitle="Remove from Whiteboard"
        dialogContent={`Remove ${
          removeTarget?.name.split(" ")[0] ?? ""
        } from this whiteboard? They will immediately lose access to it.`}
        confirmBtnText="Remove"
        shouldShowCancelButton
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveTarget(null)}
      />
      <Menu
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        position="bottom-start"
        target={
          <CounterButton
            active={open}
            label={String(boardMembers.length)}
            icon={<PeopleIcon />}
            className={open ? "open" : undefined}
            onClearAll={() => undefined}
          />
        }
      >
        <StyledPeopleMenu>
          <MenuHeader
            title="People"
            rightElement={
              <IconButton
                size="small"
                variant="text gray"
                onClick={handleDialogOpen}
              >
                <AddCrossTinyIcon />
              </IconButton>
            }
          />
          <div className="people-scroll">
            <PeopleMenuSection
              label="Members"
              people={members}
              managerId={managerId}
              onMakeManager={handleMakeManager}
              onRemove={handleRemove}
            />
            {clients.length > 0 ? (
              <PeopleMenuSection
                label="Clients"
                people={clients}
                managerId={managerId}
                onMakeManager={handleMakeManager}
                onRemove={handleRemove}
              />
            ) : null}
          </div>
        </StyledPeopleMenu>
      </Menu>
    </>
  );
};
